
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp, TrendingDown, Euro } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface StockData {
  symbol: string;
  name: string;
  price: number;
  change: number;
}

const MarketTicker = () => {
  // Use mock data for French stocks
  const { data, isLoading } = useQuery({
    queryKey: ['frenchStocks'],
    queryFn: async (): Promise<StockData[]> => {
      // In a real implementation, we would fetch from a stock API
      // Since we're removing the CoinGecko dependency, we'll use mock data
      return [
        { symbol: 'CAC40', name: 'CAC 40', price: 8250.32, change: 0.75 },
        { symbol: 'BNP.PA', name: 'BNP Paribas', price: 62.45, change: 1.25 },
        { symbol: 'AIR.PA', name: 'Airbus', price: 152.88, change: -0.32 },
        { symbol: 'MC.PA', name: 'LVMH', price: 785.40, change: 0.92 },
        { symbol: 'SAN.PA', name: 'Sanofi', price: 87.65, change: -0.18 },
        { symbol: 'ORA.PA', name: 'Orange', price: 10.92, change: 0.45 }
      ];
    },
    refetchInterval: 60000, // Refresh every minute
  });

  // Format price with commas for thousands
  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR',
      maximumFractionDigits: 2
    }).format(price);
  };

  return (
    <div className="bg-black/60 backdrop-blur-sm z-40 py-2 relative">
      <ScrollArea className="w-full overflow-hidden">
        <div className="animate-marquee whitespace-nowrap flex items-center px-4">
          {isLoading ? (
            <span className="text-white">Chargement des données du marché...</span>
          ) : (
            <>
              {data?.map((stock, index) => (
                <div key={index} className="inline-flex items-center mr-12">
                  {stock.change >= 0 ? (
                    <TrendingUp className="h-5 w-5 mr-2 text-patrimoine-gold" />
                  ) : (
                    <TrendingDown className="h-5 w-5 mr-2 text-patrimoine-gold" />
                  )}
                  <span className="font-medium text-white">{stock.symbol}:</span>
                  <span className="ml-2 text-white">{formatPrice(stock.price)}</span>
                  <span className={`ml-2 ${stock.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {stock.change >= 0 ? '+' : ''}{stock.change.toFixed(2)}%
                  </span>
                </div>
              ))}
              
              {/* Duplicate items for continuous scrolling effect */}
              {data?.map((stock, index) => (
                <div key={`dup-${index}`} className="inline-flex items-center mr-12">
                  {stock.change >= 0 ? (
                    <TrendingUp className="h-5 w-5 mr-2 text-patrimoine-gold" />
                  ) : (
                    <TrendingDown className="h-5 w-5 mr-2 text-patrimoine-gold" />
                  )}
                  <span className="font-medium text-white">{stock.symbol}:</span>
                  <span className="ml-2 text-white">{formatPrice(stock.price)}</span>
                  <span className={`ml-2 ${stock.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {stock.change >= 0 ? '+' : ''}{stock.change.toFixed(2)}%
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default MarketTicker;
